import { format, parseISO, isValid } from 'date-fns';
import type { Agent } from '@/types/Agents';

export type SubscriptionStatus = 'active' | 'requested' | 'expired';

export interface SubscriptionPlan {
  id: number;
  name: string;
  price: number;
  currency: string;
  billing_period: 'monthly' | 'yearly';
  included_minutes: number;
  overage_price_per_minute: number;
  crm_included: boolean;
}

export interface SubscriptionStatusMeta {
  labelKey: string;
  defaultLabel: string;
  badgeVariant: 'default' | 'secondary' | 'outline' | 'destructive';
  sortOrder: number;
}

export interface UsageMetrics {
  usageSeconds: number;
  includedMinutes: number | null;
  overageSeconds: number;
  estimatedOverageAmount: number;
  overagePricePerMinute: number;
  billingExempt: boolean;
  hasUsage: boolean;
  usagePercent: number | null;
}

const STATUS_META: Record<SubscriptionStatus, SubscriptionStatusMeta> = {
  active: {
    labelKey: 'billing.status.active.translation',
    defaultLabel: 'Active',
    badgeVariant: 'default',
    sortOrder: 1,
  },
  requested: {
    labelKey: 'billing.status.requested.translation',
    defaultLabel: 'Requested',
    badgeVariant: 'outline',
    sortOrder: 0,
  },
  expired: {
    labelKey: 'billing.status.expired.translation',
    defaultLabel: 'Expired',
    badgeVariant: 'secondary',
    sortOrder: 3,
  },
};

const NO_SUBSCRIPTION_META: SubscriptionStatusMeta = {
  labelKey: 'billing.status.none.translation',
  defaultLabel: 'No subscription',
  badgeVariant: 'outline',
  sortOrder: 5,
};

export function getSubscriptionStatusMeta(
  status?: SubscriptionStatus | string
): SubscriptionStatusMeta {
  if (!status) return NO_SUBSCRIPTION_META;
  return STATUS_META[status as SubscriptionStatus] ?? NO_SUBSCRIPTION_META;
}

export function getAgentSubscriptionSortOrder(agent: Agent): number {
  return getSubscriptionStatusMeta(agent.subscription?.status).sortOrder;
}

export function sortAgentsBySubscription(agents: Agent[]): Agent[] {
  return [...agents].sort((a, b) => {
    const orderDiff =
      getAgentSubscriptionSortOrder(a) - getAgentSubscriptionSortOrder(b);
    if (orderDiff !== 0) return orderDiff;
    return (a.name ?? '').localeCompare(b.name ?? '');
  });
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const date = parseISO(value);
    if (!isValid(date)) return null;
    return format(date, 'd MMM yyyy');
  } catch {
    return null;
  }
}

export type BillingCycleDisplay =
  | { type: 'requested'; date: string | null }
  | { type: 'range'; start: string; end: string }
  | { type: 'started'; date: string }
  | { type: 'renews'; date: string }
  | { type: 'not_set' };

export function getBillingCycleDisplay(
  activatedAt: string | null | undefined,
  expiringAt: string | null | undefined,
  status?: SubscriptionStatus | string
): BillingCycleDisplay {
  const start = formatDate(activatedAt);
  const end = formatDate(expiringAt);

  if (status === 'requested') {
    return { type: 'requested', date: start };
  }

  if (start && end) {
    return { type: 'range', start, end };
  }

  if (start) {
    return { type: 'started', date: start };
  }

  if (end) {
    return { type: 'renews', date: end };
  }

  return { type: 'not_set' };
}

export function getUsageMetrics(
  subscription: Agent['subscription'] | undefined
): UsageMetrics {
  const usageSeconds = subscription?.current_usage_seconds ?? 0;
  const includedMinutes = subscription?.included_minutes ?? null;
  const includedSeconds = includedMinutes == null ? null : includedMinutes * 60;
  const overageSeconds =
    subscription?.overage_seconds ??
    (includedSeconds == null ? 0 : Math.max(usageSeconds - includedSeconds, 0));
  const overagePricePerMinute = subscription?.overage_price_per_minute ?? 0;
  const billingExempt = subscription?.billing_exempt === true;
  const estimatedOverageAmount = billingExempt
    ? 0
    : (subscription?.estimated_overage_amount ??
      Math.round((overageSeconds * overagePricePerMinute * 100) / 60) / 100);
  const usagePercent =
    includedSeconds != null && includedSeconds > 0
      ? Math.min(100, (usageSeconds / includedSeconds) * 100)
      : null;

  return {
    usageSeconds,
    includedMinutes,
    overageSeconds,
    estimatedOverageAmount,
    overagePricePerMinute,
    billingExempt,
    hasUsage:
      subscription != null && (includedMinutes != null || usageSeconds > 0),
    usagePercent,
  };
}

export function findPlanByName(
  plans: SubscriptionPlan[],
  planName?: string
): SubscriptionPlan | undefined {
  if (!planName) return undefined;
  const normalized = planName.trim().toLowerCase();
  return plans.find(p => p.name.trim().toLowerCase() === normalized);
}

export function isActiveSubscription(status?: string): boolean {
  return status === 'active';
}

export function canRequestSubscription(status?: string): boolean {
  return !status || status === 'expired';
}

export type AgentReadiness = 'ready' | 'waiting' | 'action_required';

/**
 * Whether an agent can actually take calls, and if not, whether that is on us
 * (a pending request) or on the owner (no subscription yet, or an expired one).
 */
export function getAgentReadiness(agent: Agent): AgentReadiness {
  const status = agent.subscription?.status;
  if (status === 'active') return 'ready';
  if (status === 'requested') return 'waiting';
  return 'action_required';
}
